import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, Trash2, ArrowUp, ArrowDown, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const SPAN_OPTIONS = [
  { value: "", label: "Normal (1×1)" },
  { value: "col-span-2", label: "Wide (2×1)" },
  { value: "row-span-2", label: "Tall (1×2)" },
  { value: "col-span-2 row-span-2", label: "Feature (2×2)" },
];

export default function GalleryManager() {
  const [images, setImages] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ url: "", alt: "", span: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/gallery");
      setImages(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function addImage(e) {
    e.preventDefault();
    if (!form.url.trim()) {
      toast.error("Image URL is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/gallery", {
        url: form.url.trim(),
        alt: form.alt.trim(),
        span: form.span || null,
        active: true,
      });
      setImages((prev) => [...(prev || []), data]);
      setForm({ url: "", alt: "", span: "" });
      setAddOpen(false);
      toast.success("Image added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function removeImage(img) {
    if (!confirm(`Delete this image?\n\n"${img.alt || img.url}"`)) return;
    try {
      await adminApi.delete(`/admin/gallery/${img.id}`);
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function move(idx, dir) {
    if (!images) return;
    const target = idx + dir;
    if (target < 0 || target >= images.length) return;
    const a = images[idx];
    const b = images[target];
    // Swap positions in local state
    const next = [...images];
    next[idx] = b;
    next[target] = a;
    setImages(next);
    try {
      await Promise.all([
        adminApi.patch(`/admin/gallery/${a.id}`, { position: b.position }),
        adminApi.patch(`/admin/gallery/${b.id}`, { position: a.position }),
      ]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  }

  async function toggleActive(img) {
    try {
      const { data } = await adminApi.patch(`/admin/gallery/${img.id}`, {
        active: !img.active,
      });
      setImages((prev) => prev.map((i) => (i.id === img.id ? data : i)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function updateSpan(img, span) {
    try {
      const { data } = await adminApi.patch(`/admin/gallery/${img.id}`, {
        span: span || null,
      });
      setImages((prev) => prev.map((i) => (i.id === img.id ? data : i)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="gallery-manager" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-[var(--trio-muted)] max-w-xl">
            The photos guests see on the gallery page. Add via URL, delete, toggle live/hidden, or reorder.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          data-testid="gallery-add-open"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2.5 text-xs tracking-widest uppercase font-bold transition-colors whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          Add Image
        </button>
      </div>

      {images === null ? (
        <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : images.length === 0 ? (
        <div className="border border-dashed border-[var(--trio-border)] p-12 text-center">
          <ImagePlus className="w-8 h-8 text-[var(--trio-muted)] mx-auto mb-3" />
          <div className="text-sm text-[var(--trio-muted)]">
            No images yet. Add your first one with the button above.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img, idx) => (
            <div
              key={img.id}
              data-testid={`gallery-item-${img.id}`}
              className={`border ${
                img.active ? "border-[var(--trio-border)]" : "border-red-500/40 opacity-70"
              } bg-[var(--trio-surface)] overflow-hidden`}
            >
              <div className="aspect-[4/3] bg-[var(--trio-bg)] relative">
                <img
                  src={img.url}
                  alt={img.alt || ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0.3";
                  }}
                />
                {!img.active && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 tracking-widest uppercase font-bold">
                    Hidden
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Move up"
                    data-testid={`gallery-up-${img.id}`}
                    className="p-1.5 bg-black/60 text-white hover:bg-black disabled:opacity-30"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === images.length - 1}
                    title="Move down"
                    data-testid={`gallery-down-${img.id}`}
                    className="p-1.5 bg-black/60 text-white hover:bg-black disabled:opacity-30"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="p-3 space-y-2 text-xs">
                <div className="text-[var(--trio-muted)] truncate" title={img.alt}>
                  {img.alt || <span className="italic">(no caption)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={img.span || ""}
                    onChange={(e) => updateSpan(img, e.target.value)}
                    data-testid={`gallery-span-${img.id}`}
                    className="flex-1 bg-[var(--trio-bg)] border border-[var(--trio-border)] px-2 py-1 text-xs rounded-none focus:outline-none focus:border-[var(--trio-primary)]"
                  >
                    {SPAN_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => toggleActive(img)}
                    data-testid={`gallery-toggle-${img.id}`}
                    className={`px-2 py-1 text-[10px] tracking-widest uppercase font-bold border ${
                      img.active
                        ? "border-[var(--trio-border)] text-[var(--trio-muted)] hover:text-[var(--trio-text)]"
                        : "border-red-500 text-red-500"
                    }`}
                    title={img.active ? "Hide from site" : "Show on site"}
                  >
                    {img.active ? "Live" : "Hidden"}
                  </button>
                  <button
                    onClick={() => removeImage(img)}
                    data-testid={`gallery-delete-${img.id}`}
                    className="p-1.5 text-[var(--trio-muted)] hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          data-testid="gallery-add-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              Add Gallery Image
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--trio-muted)]">
              Paste an image URL (upload elsewhere first — Emergent's asset uploader,
              Imgur, or your Google Drive share link).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={addImage} className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase">Image URL</Label>
              <Input
                type="url"
                required
                autoFocus
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…"
                data-testid="gallery-add-url"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Caption / Alt Text</Label>
              <Input
                value={form.alt}
                onChange={(e) => setForm({ ...form, alt: e.target.value })}
                placeholder="Fried catfish plate with mac, greens, cornbread"
                data-testid="gallery-add-alt"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
              <div className="text-[11px] text-[var(--trio-muted)] mt-1">
                Describe what's in the photo — helps blind guests + Google.
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Size on grid</Label>
              <select
                value={form.span}
                onChange={(e) => setForm({ ...form, span: e.target.value })}
                data-testid="gallery-add-span"
                className="mt-2 w-full bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm rounded-none focus:outline-none focus:border-[var(--trio-primary)]"
              >
                {SPAN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-[var(--trio-muted)] mt-1">
                Use Feature or Wide sparingly to highlight the best shots.
              </div>
            </div>

            {form.url && (
              <div className="border border-[var(--trio-border)] p-2">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] mb-1">
                  Preview
                </div>
                <img
                  src={form.url}
                  alt="preview"
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0.3";
                  }}
                />
              </div>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                data-testid="gallery-add-cancel"
                className="px-5 py-2 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="gallery-add-submit"
                className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? "Adding…" : "Add"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
